import type { LocationVenueRate, LocationVenueRates } from '@/models/LocationVenueRates';
import { formatCurrency } from '@/utils/translationHelper';
import { DestinationGuide } from '@/models/DestinationGuide';
import { type DropdownOptionShape } from '@cvent/carina/components/Dropdown';
import { LOCATION } from '@/constants/venueRatesConstants';
import type { Locale } from '@/i18n';

type IndexData = {
  startIndex: number | undefined;
  endIndex: number | undefined;
};

export type CityData = {
  value?: number;
  category?: string;
  group?: string;
};

export type FormattedData = {
  data: CityData[];
  groups: string[];
  categories: string[];
  yAxisDataPoints: number[];
  yAxisMax: number;
  yAxisMin: number;
  currencyCode: string | undefined;
};

type MinMax = {
  min: number;
  max: number;
};

interface YAxisDataPoints {
  plotPoints: number[];
  yAxisMin: number;
  yAxisMax: number;
}

export type TableCityDataType = {
  Location: string;
  rowName: string;
  dgData: DestinationGuide;
  locationName: string;
  rowNumber: number;
};

type TableFormat = {
  cityData: TableCityDataType;
  monthList: string[];
};

export type FormatTableData = {
  data: TableCityDataType[];
  tableHeaderMonths: string[];
};

export type LineProps = {
  name: string;
  data?: CityData[];
  showLegend?: boolean;
  axisMin?: number;
  axisMax?: number;
  height: number;
  xAxisTickValues?: number[] | undefined;
  yAxisTickValues?: number[];
  colorSeed?: string;
  tooltip?: string;
  categories?: string[];
  groups?: string[];
  maxGroups: number | undefined;
  options?: Record<string, unknown>;
  formatters?: Record<string, (...args: string[]) => string>;
};

export function getStartAndEndMonthIndex(
  venueRatesData: LocationVenueRates,
  numberOfMonths: number,
  eventDate?: Date
): IndexData {
  const startIndex = 0;
  const endIndex = 0;

  if (venueRatesData.length < 1) {
    return {
      startIndex,
      endIndex
    };
  }

  const maxMonthsData = venueRatesData?.at(0)?.venueRates?.futureRates?.length ?? 18;
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const identifiedDate = eventDate || currentDate;
  const eventYear = identifiedDate.getFullYear();
  const eventMonth = identifiedDate.getMonth() + 1;

  if (!eventDate) {
    return {
      startIndex: 0,
      endIndex: numberOfMonths - 1
    };
  }
  let monthYear = venueRatesData
    ? venueRatesData
        ?.at(0)
        ?.venueRates?.futureRates?.slice(0, numberOfMonths / 2)
        ?.pop()
    : { month: currentMonth, year: currentYear };
  let month = monthYear?.month;
  let year = monthYear?.year;
  if ((month && eventYear === year && eventMonth <= month) || (year && eventYear < year)) {
    return {
      startIndex: 0,
      endIndex: numberOfMonths - 1
    };
  }
  // Find the month and year from future rates for the case if event date lies between
  // (numbereOfMonths/2) and last record of future rates.
  monthYear = venueRatesData
    ? venueRatesData?.at(0)?.venueRates?.futureRates?.slice((numberOfMonths / 2 + 1) * -1)?.[0]
    : { month: currentMonth, year: currentYear };
  month = monthYear?.month;
  year = monthYear?.year;
  if ((month && eventYear === year && eventMonth >= month) || (year && eventYear > year)) {
    return {
      endIndex: maxMonthsData - 1,
      startIndex: maxMonthsData - numberOfMonths
    };
  }

  const date = new Date(eventDate);
  date.setMonth(date.getMonth() - (numberOfMonths / 2 - 1));
  const index = venueRatesData
    ? venueRatesData
        ?.at(0)
        ?.venueRates?.futureRates?.findIndex(
          rate => rate.month === date.getMonth() + 1 && rate.year === date.getFullYear()
        )
    : 0;
  return {
    startIndex: index,
    endIndex: index && index + numberOfMonths - 1
  };
}

export function convertDataToModalChart(
  venueRatesData: LocationVenueRates,
  identifiedStartMonthIndex: number | undefined,
  identifiedEndMonthIndex: number | undefined,
  yAxisTicks: number
): FormattedData | null {
  if (venueRatesData.length < 1 || identifiedStartMonthIndex === null || identifiedEndMonthIndex === null) {
    return null;
  }
  let data: CityData[] = [];
  const categories: string[] = [];
  for (let i = 0; i < venueRatesData.length; i++) {
    data = data.concat(convertDataToChartFormat(venueRatesData, i, identifiedStartMonthIndex, identifiedEndMonthIndex));
    categories.push(venueRatesData[i].location.name);
  }
  const groups = getXAxisDataPoints(data, identifiedEndMonthIndex, identifiedStartMonthIndex);
  const { max, min } = getMaxMinDataPoint(data);

  const yAxisDataPoints = getYAxisDataPoints(max, min, yAxisTicks);
  const currencyCode = 'USD';
  const formattedData = {
    data,
    groups,
    categories,
    yAxisDataPoints: yAxisDataPoints.plotPoints,
    yAxisMax: yAxisDataPoints.yAxisMax,
    yAxisMin: yAxisDataPoints.yAxisMin,
    currencyCode
  };
  return formattedData;
}

/**
 * Converter which return formatted data for chart view in venue rates modal.
 * @param venueRatesData
 * @param identifiedStartMonthIndex
 * @param identifiedEndMonthIndex
 * @param yAxisTicks
 */
export function convertDataToSearchPageChart(
  venueRatesData: LocationVenueRates,
  identifiedStartMonthIndex: number | undefined,
  identifiedEndMonthIndex: number | undefined,
  yAxisTicks: number
): FormattedData | null {
  if (venueRatesData.length < 1 || identifiedStartMonthIndex === null || identifiedEndMonthIndex === null) {
    return null;
  }
  const categories: string[] = [];
  const data = convertDataToChartFormat(venueRatesData, 0, identifiedStartMonthIndex, identifiedEndMonthIndex);
  const location = venueRatesData?.at(0)?.location;
  if (location && location.name) {
    categories.push(location.name);
  }
  const groups = getXAxisDataPoints(data, identifiedEndMonthIndex, identifiedStartMonthIndex);
  const { max, min } = getMaxMinDataPoint(data);

  const yAxisDataPoints = getYAxisDataPoints(max, min, yAxisTicks);
  const currencyCode = 'USD';
  const formattedData = {
    data,
    groups,
    categories,
    yAxisDataPoints: yAxisDataPoints.plotPoints,
    yAxisMax: yAxisDataPoints.yAxisMax,
    yAxisMin: yAxisDataPoints.yAxisMin,
    currencyCode
  };
  return formattedData;
}

/**
 * Convertor for Formatting venuesRates data for chart view
 * @param venueRatesData
 * @param startIndex
 * @param endIndex
 */
function convertDataToChartFormat(
  venueRatesData: LocationVenueRates,
  cityIndex: number,
  startIndex: number | undefined,
  endIndex: number | undefined
): CityData[] {
  const cityData: CityData[] = [];
  for (let i = startIndex; i !== undefined && endIndex !== undefined && i <= endIndex; i++) {
    const rates = venueRatesData?.at(cityIndex)?.venueRates?.futureRates?.at(i);
    const date = dateFormatter(rates?.year, rates?.month);

    cityData.push({
      value: rates?.averageRate ? round(rates.averageRate) : null,
      category: venueRatesData?.at(cityIndex)?.location.name,
      group: date
    });
  }
  return cityData;
}

export const extractSelectedMonth = (index: number, dataList: DropdownOptionShape[]) => {
  for (let i = 0; i < dataList.length; i++) {
    if (parseInt(dataList[i].value, 10) === index) {
      return dataList[i];
    }
  }
  return null;
};

export const dateFormatter = (year, month) => {
  let date = year ? `${year}-` : '1990-';
  date += month ? `${month}`.padStart(2, '0') : '';
  return date;
};

export const round = price => {
  return price ? Math.round(price) : price;
};

function getXAxisDataPoints(
  data: CityData[],
  indentifyEndMonthIndex: number | undefined,
  indentifyStartMonthIndex: number | undefined
) {
  const groups: string[] = [];
  for (
    let i = 0;
    indentifyStartMonthIndex !== undefined &&
    indentifyEndMonthIndex !== undefined &&
    i <= indentifyEndMonthIndex - indentifyStartMonthIndex;
    i++
  ) {
    const currentGroup = data[i]?.group;
    if (currentGroup) {
      groups.push(currentGroup);
    }
  }
  return groups;
}

const minUsingReduce = array => array.reduce((min, currentValue) => Math.min(min, currentValue), array[0]);
const maxUsingReduce = array => array.reduce((max, currentValue) => Math.max(max, currentValue), array[0]);

// get max and min venue rates from list of data need to plot on chart
function getMaxMinDataPoint(data: CityData[]): MinMax {
  const array = data.filter(obj => obj.value != null).map(obj => obj.value);
  const min = minUsingReduce(array);
  const max = maxUsingReduce(array);

  return {
    min,
    max
  };
}

// get y axis data point against which chart need to plot
function getYAxisDataPoints(max: number, min: number, numberOfTicks: number): YAxisDataPoints {
  const minTick = min > 10 ? min - (min % 10) - 10 : 0;
  const maxTick = max + (10 - (max % 10)) + 10;

  let delta = Math.floor((maxTick - minTick) / (numberOfTicks - 1));
  delta = delta > 10 ? delta + (10 - (delta % 10)) : 10;
  const plotPoints: number[] = [];
  let tempData = minTick;
  for (let i = 0; i <= numberOfTicks - 1; i++) {
    plotPoints.push(tempData);
    tempData += delta;
  }

  return {
    plotPoints,
    yAxisMin: plotPoints[0],
    yAxisMax: plotPoints[plotPoints.length - 1]
  };
}

/**
 * Converting venue rates data into table formatted data
 * @param venueRatesData
 * @param startIndex
 * @param endIndex
 * @param rowNumber
 * @param location
 */
const convertDataToTableFormat = (
  venueRatesData: LocationVenueRate,
  startIndex: number,
  endIndex: number,
  rowNumber: number,
  locale: Locale
): TableFormat => {
  const stateOrCountry = venueRatesData.location.stateCode || venueRatesData.location.countryName;
  const cityData: TableCityDataType = {
    Location: `${venueRatesData.location.name}, ${stateOrCountry}`,
    rowName: `${venueRatesData.location.name}, ${stateOrCountry}`,
    dgData: venueRatesData.destinationGuideExists ? venueRatesData.destinationGuide : {},
    locationName: venueRatesData.location.name,
    rowNumber
  };

  const monthList: string[] = [];
  monthList.push(LOCATION);
  for (let i = startIndex; i <= endIndex; i++) {
    const rates =
      venueRatesData && venueRatesData?.venueRates && venueRatesData?.venueRates?.futureRates
        ? venueRatesData?.venueRates?.futureRates[i]
        : undefined;
    monthList.push(`${getMonthName(rates?.month as number, locale, rates?.year, true).slice(0, 3)} ${rates?.year}`);
    cityData[
      `${getMonthName(rates?.month as number, locale, rates?.year, true).slice(0, 3)} ${rates?.year}`.replace(
        /\s+/g,
        ''
      )
    ] = rates?.averageRate ? `${formatCurrency(locale, round(rates.averageRate), 'USD', 0, 0)}` : ' - ';
  }
  return {
    cityData,
    monthList
  };
};

export const formatTableData = (
  venueRatesData: LocationVenueRates,
  identifiedStartMonthIndex: number,
  identifiedEndMonthIndex: number,
  locale: Locale
): FormatTableData | null => {
  if (venueRatesData.length < 1 || identifiedStartMonthIndex === null || identifiedEndMonthIndex === null) {
    return null;
  }
  let data: TableCityDataType[] = [];
  let tableHeaderMonths: string[] = [];

  for (let i = 0; i < venueRatesData?.length; i++) {
    const { monthList, cityData } = convertDataToTableFormat(
      venueRatesData[i],
      identifiedStartMonthIndex,
      identifiedEndMonthIndex,
      i + 1,
      locale
    );
    data = data.concat(cityData);
    tableHeaderMonths = monthList;
  }

  return {
    data,
    tableHeaderMonths
  };
};

const getMonthName = (monthNumber: number, locale: Locale, year?: number, withoutYear?: boolean) => {
  const resolvedYear = year || 1990; // logic from old search
  const date = new Date(resolvedYear, monthNumber - 1, 2);
  return date.toLocaleString(
    locale,
    withoutYear
      ? {
          month: 'long',
          timeZone: 'UTC'
        }
      : {
          month: 'long',
          year: 'numeric',
          timeZone: 'UTC'
        }
  );
};

/**
 * Creates dropdown data for venueRates table component
 * @param venueRates
 * @param startIndex
 */
export const getDropdownData = (
  venueRates: LocationVenueRates,
  startIndex: number,
  locale: Locale
): DropdownOptionShape[] => {
  if (venueRates?.length < 1 || startIndex < 0) {
    return [];
  }
  const dropdownData: DropdownOptionShape[] = [];
  const futureRates = venueRates.at(0)?.venueRates.futureRates;
  if (!futureRates || futureRates.length < 1) {
    return [];
  }

  if (startIndex >= futureRates.length) {
    const { year } = futureRates[futureRates.length - 1];
    const { month } = futureRates[futureRates.length - 1];
    const label = `${getMonthName(month as number, locale, year)}`;
    dropdownData.push({ label, value: (futureRates.length - 1) as unknown as string });
    return dropdownData;
  }
  for (let i = startIndex; i < futureRates.length; i++) {
    const label = `${getMonthName(futureRates[i].month as number, locale, futureRates[i].year)}`;
    dropdownData.push({ label, value: i as unknown as string });
  }

  return dropdownData;
};
